/**
 * QuickAttendanceWizard Component
 * Multi-step wizard for quick patient attendance
 *
 * @version 2025.1.0
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  PlayArrow as StartIcon,
} from '@mui/icons-material';
import { QuickAttendanceWizardProps } from './QuickAttendanceWizard.types';
import { useQuickAttendance } from './hooks/useQuickAttendance';
import { ConfirmationStep } from './steps/ConfirmationStep';
import { TriageStep } from './steps/TriageStep';
import { StartAttendanceStep } from './steps/StartAttendanceStep';
import { AttendanceStatus } from '../../../../types/reception';

/**
 * QuickAttendanceWizard - Main wizard dialog component
 * Combines check-in, triage, and start attendance in one flow
 */
export const QuickAttendanceWizard: React.FC<QuickAttendanceWizardProps> = ({
  open,
  appointment,
  onClose,
  onComplete,
  checkIn,
  startAttendance,
  completeAttendance,
}) => {
  const {
    state,
    isFirstStep,
    currentStepKey,
    handleNext,
    handleBack,
    handleSkipTriage,
    setConfirmed,
    setTriageData,
  } = useQuickAttendance({
    appointment,
    checkIn,
    startAttendance,
    completeAttendance,
    onComplete,
    onClose,
  });

  if (!appointment) return null;

  const needsCheckIn = appointment.attendanceStatus === AttendanceStatus.NOT_ATTENDED;

  // Determine if next button should be disabled
  const isNextDisabled = () => {
    if (state.loading) return true;
    if (currentStepKey === 'confirmation' && !state.confirmed) return true;
    return false;
  };

  // Get appropriate button text
  const getNextButtonText = () => {
    if (state.loading) return 'Processando...';
    if (currentStepKey === 'start') return 'Iniciar Atendimento';
    if (currentStepKey === 'confirmation' && needsCheckIn) return 'Confirmar e Continuar';
    return 'Próximo';
  };

  // Get appropriate button icon
  const getNextButtonIcon = () => {
    if (state.loading) return <CircularProgress size={20} />;
    if (currentStepKey === 'start') return <StartIcon />;
    return <NextIcon />;
  };

  // Render current step content
  const renderStepContent = () => {
    switch (currentStepKey) {
      case 'confirmation':
        return (
          <ConfirmationStep
            appointment={appointment}
            confirmed={state.confirmed}
            onConfirmedChange={setConfirmed}
            needsCheckIn={needsCheckIn}
          />
        );
      case 'triage':
        return (
          <TriageStep
            triageData={state.triageData}
            onTriageDataChange={setTriageData}
            onSkip={handleSkipTriage}
          />
        );
      case 'start':
        return (
          <StartAttendanceStep
            appointment={appointment}
            triageData={state.triageData}
            queueNumber={state.assignedQueueNumber}
            checkInPerformed={state.checkInPerformed}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={state.loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={state.loading}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          Atendimento Rapido
          <IconButton
            size="small"
            onClick={onClose}
            disabled={state.loading}
            aria-label="Fechar"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Stepper */}
        <Stepper activeStep={state.activeStep} sx={{ mb: 3 }}>
          {state.steps.map((step) => (
            <Step key={step.key}>
              <StepLabel optional={step.optional ? 'Opcional' : undefined}>
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Error Alert */}
        {state.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {state.error}
          </Alert>
        )}

        {/* Step Content */}
        <Box sx={{ minHeight: 300 }}>{renderStepContent()}</Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleBack}
          disabled={state.loading}
          startIcon={isFirstStep ? undefined : <BackIcon />}
        >
          {isFirstStep ? 'Cancelar' : 'Voltar'}
        </Button>
        <Button
          onClick={handleNext}
          variant="contained"
          color={currentStepKey === 'start' ? 'success' : 'primary'}
          disabled={isNextDisabled()}
          endIcon={getNextButtonIcon()}
        >
          {getNextButtonText()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickAttendanceWizard;
